import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LessonWorkspaceMode } from "../auth/api-client";
import { LessonIcon } from "./LessonIcon";
import {
  LESSON_ICON_NAMES,
  formatLessonIconLabel,
  lessonIconDefinition,
} from "./lessonIcons";

export interface LessonIconPickerModalProps {
  readonly open: boolean;
  readonly heading?: string;
  readonly selectedIconName: string | undefined;
  readonly bgColor?: string;
  readonly fgColor?: string;
  readonly lessonId: string;
  readonly title: string;
  readonly workspaceMode?: LessonWorkspaceMode;
  readonly onClose: () => void;
  readonly onSelect: (iconName: string | undefined) => void;
}

export function LessonIconPickerModal({
  open,
  heading = "Choose lesson icon",
  selectedIconName,
  bgColor,
  fgColor,
  lessonId,
  title,
  workspaceMode,
  onClose,
  onSelect,
}: LessonIconPickerModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
    if (open) {
      setSearch("");
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  const filteredIcons = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return LESSON_ICON_NAMES;
    return LESSON_ICON_NAMES.filter((name) => formatLessonIconLabel(name).includes(q) || name.includes(q));
  }, [search]);

  const previewBg = bgColor?.trim() || "#1b4d2e";
  const previewFg = fgColor?.trim() || "#fdf9f3";

  function pick(iconName: string | undefined) {
    onSelect(iconName);
    onClose();
  }

  return (
    <dialog
      ref={dialogRef}
      className="teacher-modal lesson-icon-picker-modal"
      onClose={onClose}
      onClick={(ev) => {
        if (ev.target === dialogRef.current) onClose();
      }}
    >
      <div className="teacher-modal-card lesson-icon-picker-modal-card" role="document">
        <h2 className="teacher-modal-title">{heading}</h2>
        <p className="muted small" style={{ marginTop: 0 }}>
          Pick an icon for your library tile, or use a gradient with the lesson&apos;s initial.
        </p>

        <label className="field">
          <span className="field-label">Search icons</span>
          <input
            ref={searchRef}
            className="input"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
            placeholder="Search by name…"
          />
        </label>

        <div className="lesson-icon-picker-grid" role="listbox" aria-label="Lesson icons">
          <button
            type="button"
            role="option"
            aria-selected={!selectedIconName}
            className={`lesson-icon-picker-option${!selectedIconName ? " is-selected" : ""}`}
            onClick={() => pick(undefined)}
          >
            <LessonIcon
              lessonId={lessonId}
              title={title}
              workspaceMode={workspaceMode}
              size={2.5}
            />
            <span className="lesson-icon-picker-label">Gradient tile</span>
          </button>

          {filteredIcons.map((name) => {
            const icon = lessonIconDefinition(name);
            if (!icon) return null;
            const selected = selectedIconName === name;
            return (
              <button
                key={name}
                type="button"
                role="option"
                aria-selected={selected}
                className={`lesson-icon-picker-option${selected ? " is-selected" : ""}`}
                title={formatLessonIconLabel(name)}
                onClick={() => pick(name)}
              >
                <span
                  className="lesson-icon-picker-swatch"
                  style={{ background: previewBg, color: previewFg }}
                  aria-hidden
                >
                  <FontAwesomeIcon icon={icon} />
                </span>
                <span className="lesson-icon-picker-label">{formatLessonIconLabel(name)}</span>
              </button>
            );
          })}
        </div>

        {filteredIcons.length === 0 ? (
          <p className="muted small lesson-icon-picker-empty">No icons match your search.</p>
        ) : null}

        <div className="teacher-modal-actions">
          <button type="button" className="btn secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </dialog>
  );
}
